import qs from "qs";

const STORAGE_KEY = "mass_ticketing_attribution";

const STANDARD_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "fbclid",
  "gclid",
];

// Non-standard marketing params worth keeping in the catch-all.
const KNOWN_EXTRA_KEYS = [
  "utm_id",
  "ttclid",
  "msclkid",
  "gbraid",
  "wbraid",
  "brid",
  "_kx",
];

const isExtraKey = (key) =>
  /^utm_/i.test(key) || /clid$/i.test(key) || KNOWN_EXTRA_KEYS.includes(key);

const parseMarketingParams = (search) => {
  const params = qs.parse(search, { ignoreQueryPrefix: true });
  const data = {};
  const extra = {};

  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value == null || value === "") {
      return;
    }
    if (STANDARD_KEYS.includes(key)) {
      data[key] = String(value);
    } else if (isExtraKey(key)) {
      extra[key] = String(value);
    }
  });

  return { data, extra };
};

const hasMarketingParams = (data, extra = {}) =>
  STANDARD_KEYS.some((key) => data[key]) || Object.keys(extra).length > 0;

const storedHasMarketing = (stored) =>
  Boolean(
    stored &&
      (STANDARD_KEYS.some((key) => stored[key]) ||
        (stored.attribution_extra &&
          typeof stored.attribution_extra === "object" &&
          Object.keys(stored.attribution_extra).length > 0))
  );

const isExternalReferrer = (referrer) => {
  if (!referrer) {
    return false;
  }
  try {
    return new URL(referrer).origin !== window.location.origin;
  } catch (e) {
    return false;
  }
};

const readStoredAttribution = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeStoredAttribution = (data) => {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
};

/**
 * Capture marketing attribution from the current URL and persist it to
 * sessionStorage.
 *
 * - UTM / click-id params are merged on every call (last-touch for campaign
 *   fields) so SPA navigations to a tagged pay URL still get stored.
 * - landing_url is set to the first URL that carried marketing params, or the
 *   first external-referrer landing when there are no params.
 * - Same-site referrers alone do not create attribution (avoids internal hops
 *   like /booking/29 -> /booking/ blocking a later ad link).
 */
export const captureAttribution = () => {
  try {
    const { data, extra } = parseMarketingParams(window.location.search);
    const marketing = hasMarketingParams(data, extra);
    const referrer = document.referrer || "";
    const existing = readStoredAttribution();

    if (marketing) {
      const merged = existing ? { ...existing } : {};

      STANDARD_KEYS.forEach((key) => {
        if (data[key]) {
          merged[key] = data[key];
        }
      });

      if (Object.keys(extra).length > 0) {
        merged.attribution_extra = {
          ...(merged.attribution_extra || {}),
          ...extra,
        };
      }

      if (!merged.referrer && referrer) {
        merged.referrer = referrer;
      }

      if (!merged.landing_url || !storedHasMarketing(existing)) {
        merged.landing_url = window.location.href || "";
      }

      writeStoredAttribution(merged);
      return;
    }

    if (existing) {
      return;
    }

    if (!isExternalReferrer(referrer)) {
      return;
    }

    writeStoredAttribution({
      referrer,
      landing_url: window.location.href || "",
    });
  } catch (e) {
    // sessionStorage may be unavailable (private mode); ignore.
  }
};

/** Return the stored attribution object, or null if none captured. */
export const getAttribution = () => readStoredAttribution();
